import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { Paper, PromptConfirmation } from 'ui';
import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { companyReportAdminTranslation } from '../constants';
import { CompanyReportAdminState } from '../types-store';
import { CompanyReportFormSections } from './components/company-report-form-sections';
import { actionCreators } from './actions';
import { CompanyReportFormSelectorProps, selectors } from './selectors';
import { CompanyReportItem } from './types-store';
import { getCompanyReportAdminAccess } from 'app/area/company-reports-administration/get-access';
import { scrollToTop } from 'app/infrastructure/platform/routing';

type CompanyReportFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & CompanyReportAdminState
  & CompanyReportFormSelectorProps
  & typeof actionCreators;

interface CompanyReportFormState {
  showTranslations: boolean;
}

class CompanyReportFormComponent extends React.PureComponent<CompanyReportFormProps, CompanyReportFormState> {
  public static statePath: string = STATE.companyReportAdministration;

  public state: Readonly<CompanyReportFormState> = {
    showTranslations: false
  };

  public componentDidMount() {
    scrollToTop();
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      this.initForm();

    }
  }

  public componentDidUpdate(prevProps: CompanyReportFormProps) {
    if (this.props.id !== prevProps.id ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm();
    }

    if (prevProps?.form?.validation?.language !== this.props?.form?.validation?.language) {
      this.updateTranslationsToggle();
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm = () => {
    const { id, sharedState,
      createCompanyReport, loadCompanyReport, displayError, translation } = this.props;

    const companyReportAccess = getCompanyReportAdminAccess(sharedState);
    if (!companyReportAccess) {
      displayError(translation.no_access_to_company_report_admin);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(id)) {
      loadCompanyReport(id, translation);
    } else {
      createCompanyReport();
    }
  };

  private switchTranslationsToggle = (showTranslations: boolean) => this.setState({ showTranslations });

  private updateTranslationsToggle = () => {
    if (!!this.props?.form?.validation?.language) {
      this.switchTranslationsToggle(true);
    }
  };

  private saveCompanyReport = (companyReport: CompanyReportItem) => {
    const { form: { savedReports, filters } = {}, saveCompanyReport, translation } = this.props;

    const successMessage = isDefinedIncludingZero(companyReport?.id)
      ? translation.company_report_updated
      : translation.company_report_created;

    saveCompanyReport(companyReport, savedReports, filters, successMessage, translation.company_report_validation_error);
  };

  private deleteCompanyReport = () => {
    const { id, deleteCompanyReport, translation } = this.props;

    deleteCompanyReport(id, translation.company_report_deleted);
  };

  public render() {
    const { section, sharedState, form, translation,
      options, loadOptions, updateTextDescription,
      updateItem, changeLanguage, copyLanguageToAll,
      selectCurrentSavedReports, updateCurrentSavedReports, updateCurrentValidation, updateSavedReportValidation, loadSavedReportEvaluation,
      addSavedReports, updateSavedReport, deleteSavedReport, deleteAllSavedReports,
      addFilters, updateFilter, deleteFilter, deleteAllFilters, updateFiltersValidation,
      updateTextItem, updateTextItemDescription, removeText, addText,
      resetTextValidation, resetTextsValidation, removeAllTexts, updateValidation,
      updateMetadataItem, removeMetadata, addMetadata, resetMetadataValidation, removeAllMetadata,
      resetValidation, displayError, switchDisplayDisabled
    } = this.props;
    const mainLanguage = sharedState?.language?.value ?? undefined;
    const locale = sharedState?.locale || '';
    const companyReportAccess = sharedState?.access?.adminPanel?.companyReport;
    const translationAdminAccess = sharedState?.access?.adminPanel?.translation?.administrationCentral;

    return <div className="entity-view">
      <Paper componentId="code-base-paper" className={undefined}>
        {!!this.props.shouldDisplayModal &&
          <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel} />}

        {!form.languageOptions || !form.companyReportItem || form.fetching
          ? <SpinLoader />
          : <CompanyReportFormSections
            formState={form}
            mainLanguage={mainLanguage}
            saveCompanyReport={this.saveCompanyReport}
            deleteCompanyReport={this.deleteCompanyReport}
            updateItem={updateItem}
            changeLanguage={changeLanguage}
            copyLanguageToAll={copyLanguageToAll}
            options={options}
            loadOptions={loadOptions}
            addFilters={addFilters}
            updateFilter={updateFilter}
            deleteFilter={deleteFilter}
            deleteAllFilters={deleteAllFilters}
            updateFiltersValidation={updateFiltersValidation}
            selectCurrentSavedReports={selectCurrentSavedReports}
            updateCurrentSavedReports={updateCurrentSavedReports}
            updateCurrentValidation={updateCurrentValidation}
            updateSavedReportValidation={updateSavedReportValidation}
            loadSavedReportEvaluation={loadSavedReportEvaluation}
            addSavedReports={addSavedReports}
            updateSavedReport={updateSavedReport}
            deleteSavedReport={deleteSavedReport}
            deleteAllSavedReports={deleteAllSavedReports}
            switchDisplayDisabled={switchDisplayDisabled}
            updateTextDescription={updateTextDescription}

            updateTextItem={updateTextItem}
            updateTextItemDescription={updateTextItemDescription}
            removeText={removeText}
            addText={addText}
            resetTextValidation={resetTextValidation}
            resetTextsValidation={resetTextsValidation}

            removeAllTexts={removeAllTexts}

            addMetadata={addMetadata}
            removeMetadata={removeMetadata}
            removeAllMetadata={removeAllMetadata}
            updateMetadataItem={updateMetadataItem}
            resetMetadataValidation={resetMetadataValidation}

            updateValidation={updateValidation}
            resetValidation={resetValidation}
            displayError={displayError}
            section={section}
            locale={locale}
            translation={translation}
            companyReportAccess={companyReportAccess}
            translationAdminAccess={translationAdminAccess}

            showTranslations={this.state.showTranslations}
            switchTranslationsToggle={this.switchTranslationsToggle} />}
      </Paper>
    </div>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(CompanyReportFormComponent)),
  actionCreators, selectors, companyReportAdminTranslation
);

export { container as CompanyReportForm };
